import { ErrorHandler, Injectable, Injector, NgZone, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastController } from '@ionic/angular/standalone';
import * as Sentry from '@sentry/angular';
import { ChunkErrorHandler } from './chunk-error-handler';
import { isProductionBuild } from '../settings/environment.service';

/**
 * Non-enumerable flag attached to an error object once the user has already been shown a
 * specific, contextual notice for it (e.g. app.component's NavigationError toast).
 * AppErrorHandler checks this before presenting its own generic toast so the same failure
 * never double-notifies (Decision 4, SUN-692). No-op for primitives — you can't tag a string.
 */
const USER_NOTIFIED_FLAG: string = '__ssUserNotified';

export function markUserNotified(error: unknown): void {
  if (typeof error !== 'object' || error === null) {
    return;
  }

  try {
    Object.defineProperty(error, USER_NOTIFIED_FLAG, {
      value: true,
      enumerable: false,
      configurable: true,
    });
  } catch {
    // Best-effort marker; a failure here must not block the caller's own notification.
  }
}

export function isUserNotified(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  return (error as Record<string, unknown>)[USER_NOTIFIED_FLAG] === true;
}

// Extracted to module scope (SUN-692 Phase 2) so `ErrorBoundaryDirective` can tag its own
// Sentry captures with the same `portal` value as the global handler, without depending on
// AppErrorHandler as a service. The class method below delegates here unchanged.
export function portalFromPathname(): string {
  const pathname: string = window.location.pathname;

  if (pathname.startsWith('/main')) {
    return 'user';
  }
  if (pathname.startsWith('/charity')) {
    return 'charity';
  }
  if (pathname.startsWith('/business')) {
    return 'business';
  }
  return 'auth';
}

type SsTestErrorKind = 'sync' | 'promise' | 'cd-loop' | 'chart';

interface SsTestHookWindow extends Window {
  __ssTestError?: (kind: SsTestErrorKind) => void;
  __SS_CHART_POISON?: boolean;
}

const RUNTIME_ERROR_TOAST_MESSAGE: string =
  'Something went wrong. If this keeps happening, please reload the app.';
const OVERLAY_ID: string = 'ss-crash-overlay';
const OVERLAY_CLASS: string = 'ss-crash-overlay';

// Crash-loop thresholds (Decision 3): same fingerprint repeating fast, or a burst of any
// errors, both indicate change detection is poisoned and no amount of toasting will help.
const FINGERPRINT_REPEAT_THRESHOLD: number = 3;
const FINGERPRINT_REPEAT_WINDOW_MS: number = 10_000;
const TOTAL_ERROR_THRESHOLD: number = 8;
const TOTAL_ERROR_WINDOW_MS: number = 30_000;
const TOAST_THROTTLE_MS: number = 60_000;

/**
 * App-wide ErrorHandler (Layer 1 of SUN-692). Composes with ChunkErrorHandler — chunk errors
 * are forwarded unchanged, byte-identical to today's behavior — rather than rewriting it.
 * Adds Sentry portal tagging, a throttled user-facing toast for uncaught runtime errors, and
 * a plain-DOM crash-loop recovery overlay for when the same error (or a burst of errors)
 * indicates change detection itself is poisoned and Angular can no longer be trusted to render.
 *
 * Must NEVER throw: this is the handler of last resort. Every branch below is wrapped so a
 * bug here degrades to silence, never a second crash.
 */
@Injectable()
export class AppErrorHandler implements ErrorHandler {
  private readonly chunkErrorHandler: ChunkErrorHandler = inject(ChunkErrorHandler);
  private readonly toastController: ToastController = inject(ToastController);
  private readonly injector: Injector = inject(Injector);
  private readonly sentryDelegate: ErrorHandler = Sentry.createErrorHandler();

  private readonly fingerprintTimestamps: Map<string, number[]> = new Map();
  private readonly lastToastAt: Map<string, number> = new Map();
  private readonly trippedFingerprints: Set<string> = new Set();
  private allTimestamps: number[] = [];
  private crashLoopActive: boolean = false;

  constructor() {
    // Deployed dev keeps `production: false`, so this hook stays live there too — useful for
    // QA and Playwright — and is compiled out of user-facing behavior on real prod builds.
    if (!this.isProductionBuild()) {
      this.registerTestHook();
    }
  }

  handleError(error: unknown): void {
    try {
      if (this.chunkErrorHandler.isChunkLoadError(error)) {
        this.chunkErrorHandler.handleError(error);
        return;
      }

      const unwrapped: unknown = (error as { rejection?: unknown })?.rejection ?? error;

      if (unwrapped instanceof HttpErrorResponse) {
        // Components/facades already show contextual HTTP messages via ErrorHandlerUtility;
        // a global toast here would double-notify. Still capture — this is a telemetry gap
        // for uncaught HTTP rejections, not a UX one.
        this.captureNonChunkError(unwrapped);
        return;
      }

      this.handleRuntimeError(unwrapped);
    } catch {
      // This is the handler of last resort — it must never itself throw.
    }
  }

  // Testability seam (same pattern as ChunkErrorHandler.reloadPage): lets specs subclass and
  // override instead of trying to mock a module-level import.
  protected isProductionBuild(): boolean {
    return isProductionBuild();
  }

  protected reloadPage(): void {
    window.location.reload();
  }

  protected goHome(): void {
    window.location.href = '/';
  }

  private handleRuntimeError(error: unknown): void {
    // Errors a contextual UX path already notified about (NavigationError toast, showModal
    // toast) are user-action failures, not poisoned-CD loops — capture for telemetry and stop.
    // Counting them toward crash-loop thresholds would let three taps on a broken RouterLink
    // escalate a handled failure into the full-screen recovery overlay (Codex round-2 finding).
    if (isUserNotified(error)) {
      this.captureNonChunkError(error);
      return;
    }

    const fingerprint: string = this.computeFingerprint(error);
    const now: number = Date.now();
    // Read before recordAndCheckCrashLoop can add this fingerprint below — the occurrence that
    // trips the threshold is exactly the evidence Sentry needs to see; suppression starts on
    // the NEXT occurrence, not this one.
    const wasAlreadyTripped: boolean = this.trippedFingerprints.has(fingerprint);
    const justTripped: boolean = this.recordAndCheckCrashLoop(fingerprint, now);

    // Capture always happens before any UI attempt (toast or overlay) so telemetry never
    // depends on UI code succeeding. Once a fingerprint has tripped the crash loop, stop
    // re-capturing it — pure volume protection; the SDK's dedupeIntegration handles the rest.
    if (!wasAlreadyTripped) {
      this.captureNonChunkError(error);
    }

    if (justTripped) {
      this.crashLoopActive = true;
      this.trippedFingerprints.add(fingerprint);
      this.showCrashLoopOverlay();
    }

    // The overlay supersedes toasts entirely once active, regardless of which fingerprint
    // triggered it — showing both would be noisy and the overlay already owns recovery UX.
    // (User-notified errors never reach this point — they exit at the top of this method.)
    if (this.crashLoopActive) {
      return;
    }

    this.maybeShowThrottledToast(fingerprint);
  }

  private captureNonChunkError(error: unknown): void {
    try {
      Sentry.withScope((scope: Sentry.Scope): void => {
        scope.setTag('portal', this.portalFromPathname());
        this.tagOpenModal(scope);
        this.sentryDelegate.handleError(error);
      });
    } catch {
      // Telemetry must never crash the app.
    }
  }

  private portalFromPathname(): string {
    return portalFromPathname();
  }

  // REASONED DEVIATION (SUN-692 Phase 4 oversight): the plan calls for `ModalController.getTop()`
  // here, but that API is async and `handleError` must stay synchronous — an await gap in the
  // handler of last resort is unacceptable. Reading the DOM directly is synchronous and safe:
  // Ionic mounts a modal's user component as the `ion-modal` element's light-DOM first element
  // child, so that child's tag name doubles as the component selector (e.g. 'app-login').
  // Topmost = last `ion-modal` in document order. No `ion-modal` in the document at all means no
  // modal is open, so no modal tags are set. Wrapped in try/catch — telemetry must never crash
  // the handler.
  private tagOpenModal(scope: Sentry.Scope): void {
    try {
      const modals: NodeListOf<Element> = document.querySelectorAll('ion-modal');
      if (modals.length === 0) {
        return;
      }

      const topmost: Element = modals[modals.length - 1];
      const componentTag: string = topmost.firstElementChild?.tagName.toLowerCase() ?? 'unknown';

      scope.setTag('modalOpen', true);
      scope.setTag('modalComponent', componentTag);
    } catch {
      // Telemetry must never crash the handler.
    }
  }

  // Fingerprint = name + message + first stack frame, so the same failure re-thrown from the
  // same call site collapses to one bucket, while a shared error type/message from different
  // call sites (e.g. a generic TypeError) is still tracked separately.
  private computeFingerprint(error: unknown): string {
    const err: { name?: string; message?: string; stack?: string } = error as {
      name?: string;
      message?: string;
      stack?: string;
    };
    const name: string = err?.name ?? 'Error';
    const message: string = err?.message ?? String(error);
    const firstFrame: string = err?.stack?.split('\n')[1]?.trim() ?? '';

    return `${name}::${message}::${firstFrame}`;
  }

  private recordAndCheckCrashLoop(fingerprint: string, now: number): boolean {
    const fingerprintHits: number[] = (this.fingerprintTimestamps.get(fingerprint) ?? []).filter(
      (t: number) => now - t < FINGERPRINT_REPEAT_WINDOW_MS,
    );
    fingerprintHits.push(now);
    this.fingerprintTimestamps.set(fingerprint, fingerprintHits);

    this.allTimestamps = [
      ...this.allTimestamps.filter((t: number) => now - t < TOTAL_ERROR_WINDOW_MS),
      now,
    ];

    return (
      fingerprintHits.length >= FINGERPRINT_REPEAT_THRESHOLD ||
      this.allTimestamps.length >= TOTAL_ERROR_THRESHOLD
    );
  }

  private maybeShowThrottledToast(fingerprint: string): void {
    const now: number = Date.now();
    const last: number | undefined = this.lastToastAt.get(fingerprint);

    if (last !== undefined && now - last < TOAST_THROTTLE_MS) {
      return;
    }

    this.lastToastAt.set(fingerprint, now);
    void this.presentRuntimeErrorToast();
  }

  private async presentRuntimeErrorToast(): Promise<void> {
    try {
      const toast: HTMLIonToastElement = await this.toastController.create({
        message: RUNTIME_ERROR_TOAST_MESSAGE,
        color: 'warning',
        duration: 4000,
        position: 'top',
        buttons: [{ text: 'Dismiss', role: 'cancel' }],
      });
      await toast.present();
    } catch {
      // Toast presentation must never crash the app.
    }
  }

  private showCrashLoopOverlay(): void {
    try {
      if (document.getElementById(OVERLAY_ID)) {
        return;
      }

      const overlay: HTMLDivElement = document.createElement('div');
      overlay.id = OVERLAY_ID;
      overlay.className = OVERLAY_CLASS;

      const panel: HTMLDivElement = document.createElement('div');
      panel.className = `${OVERLAY_CLASS}__panel`;

      const heading: HTMLHeadingElement = document.createElement('h2');
      heading.className = `${OVERLAY_CLASS}__heading`;
      heading.textContent = "Something's gone wrong";

      const body: HTMLParagraphElement = document.createElement('p');
      body.className = `${OVERLAY_CLASS}__body`;
      body.textContent = 'The app hit repeated errors. Reloading usually fixes this.';

      const actions: HTMLDivElement = document.createElement('div');
      actions.className = `${OVERLAY_CLASS}__actions`;

      const reloadButton: HTMLButtonElement = document.createElement('button');
      reloadButton.type = 'button';
      reloadButton.className = `${OVERLAY_CLASS}__button ${OVERLAY_CLASS}__button--primary`;
      reloadButton.textContent = 'Reload';
      reloadButton.addEventListener('click', (): void => this.reloadPage());

      const homeButton: HTMLButtonElement = document.createElement('button');
      homeButton.type = 'button';
      homeButton.className = `${OVERLAY_CLASS}__button ${OVERLAY_CLASS}__button--secondary`;
      homeButton.textContent = 'Go Home';
      homeButton.addEventListener('click', (): void => this.goHome());

      actions.append(reloadButton, homeButton);
      panel.append(heading, body, actions);
      overlay.append(panel);
      document.body.append(overlay);
    } catch {
      // The recovery overlay must never itself be the thing that crashes.
    }
  }

  private registerTestHook(): void {
    try {
      const testWindow: SsTestHookWindow = window as SsTestHookWindow;

      testWindow.__ssTestError = (kind: SsTestErrorKind): void => {
        if (kind === 'chart') {
          // Phase 2 chart guards read this flag in dev builds to simulate a poisoned render
          // input; Layer 1's job is only to flip it.
          testWindow.__SS_CHART_POISON = !testWindow.__SS_CHART_POISON;
          return;
        }

        // NgZone is resolved lazily at invocation time, never injected eagerly — this hook is
        // invoked by Playwright from outside the Angular zone, and errors only reach this
        // ErrorHandler when thrown from tasks running inside it.
        const zone: NgZone = this.injector.get(NgZone);
        zone.run((): void => {
          if (kind === 'sync') {
            setTimeout((): void => {
              throw new Error('[SS test] sync error');
            }, 0);
          } else if (kind === 'promise') {
            void Promise.reject(new Error('[SS test] promise rejection'));
          } else if (kind === 'cd-loop') {
            for (let i: number = 0; i < 4; i++) {
              setTimeout((): void => {
                throw new Error('[SS test] cd-loop error');
              }, i * 50);
            }
          }
        });
      };
    } catch {
      // Test-hook registration must never break bootstrap.
    }
  }
}
